import React, { ReactNode, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import { Download, ExpandMore, Label, LocationOn, Refresh, Search, Star } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { categories as propertyCategories } from '@luxuryescapes/lib-global';

import { Autocomplete, Input, Select } from '~/components/Common/Forms/fields';

import {
  BEDBANK_COMMENTS_ENUM,
  BEDBANK_PROFILES_ENUM,
  BEDBANK_PROPERTY_DOMAINS_ENUM,
  BEDBANK_PROPERTY_DOMAIN_LE,
  BEDBANK_PROPERTY_TYPES_ENUM,
  BEDBANK_PROPERTY_TYPE_HOTEL,
} from '~/consts/bedbank';
import {
  booleanOptions,
  countriesOptions,
  guestRatingCountOptions,
  paymentMethodOptions,
  ratingOperations,
  ratingValues,
  statusOptions,
  supplierRatingOperations,
  supplierRatingOptions,
  supplierRatingSupplierOptions,
} from '~/consts/bedbankFilterOptions';
import { TAG_TYPE_HOLIDAY } from '~/consts/tags';

import OffersService from '~/services/OffersService';

const PROPERTY_TYPE_OPTIONS = [
  { label: 'All', value: '' },
  ...BEDBANK_PROPERTY_TYPES_ENUM.map((t) => ({
    label: t === 'HOTEL' ? 'Hotels & Resorts' : 'Unique Stays',
    value: t,
  })),
];

const DEFAULT_RATING_COMPARE_OPERATION = 'gte';

type CountryOption = {
  label: string;
  value: string;
};

const SEARCH_TYPE_OPTIONS = [
  { label: 'Property ID', value: 'id' },
  { label: 'External ID', value: 'externalId' },
  { label: 'Name', value: 'name' },
];

// Bedbank property ids are UUIDs — pasting one means the user is searching by property id
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Props {
  query: string;
  type: 'id' | 'name' | 'externalId';
  country: Array<string>;
  status: Array<string>;
  chain: Array<string>;
  chains: Array<string>;
  profile: string;
  ratingOp: string;
  ratingVal: string;
  city: string;
  stateProvince: string;
  domain: string;
  comment: string;
  promotions: boolean;
  googleRatingCount: string;
  googleRatingOp: string;
  googleRatingVal: string;
  paymentMethods: string;
  supplierRatingSupplier: string;
  supplierStarRatingOp: string;
  supplierStarRatingVal: string;
  supplierGuestRatingOp: string;
  supplierGuestRatingVal: string;
  supplierReviewCountMin: string;
  supplierReviewCountMax: string;
  propertyType: string;
  holidayTypes: Array<string>;
  categories: Array<string>;
  handleSubmit: (Query) => void;
  handleExportSubmit: (Query) => void;
  onReset: () => void;
  footerLeftAction?: ReactNode;
}

function SearchForm({
  query,
  type,
  country,
  status,
  chain,
  chains,
  profile,
  ratingOp,
  ratingVal,
  city,
  stateProvince,
  domain,
  comment,
  promotions,
  googleRatingCount,
  googleRatingOp,
  googleRatingVal,
  paymentMethods,
  supplierRatingSupplier,
  supplierStarRatingOp,
  supplierStarRatingVal,
  supplierGuestRatingOp,
  supplierGuestRatingVal,
  supplierReviewCountMin,
  supplierReviewCountMax,
  propertyType,
  holidayTypes,
  categories,
  handleSubmit: onSubmit,
  handleExportSubmit,
  onReset,
  footerLeftAction,
}: Props) {
  const [holidayTagOptions, setHolidayTagOptions] = useState<Array<string>>([]);

  useEffect(() => {
    OffersService.getTags(TAG_TYPE_HOLIDAY).then((data) => {
      setHolidayTagOptions(data.result.map((item: { name: string }) => item.name));
    });
  }, []);

  const { control, getValues, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      query,
      type: type ?? 'name',
      country,
      status,
      chain,
      profile,
      ratingOp: ratingOp ?? DEFAULT_RATING_COMPARE_OPERATION,
      ratingVal,
      city,
      stateProvince,
      domain,
      comment,
      promotions: promotions !== undefined ? promotions?.toString() : booleanOptions[0].value,
      googleRatingCount: googleRatingCount ?? guestRatingCountOptions[0].value,
      googleRatingOp: googleRatingOp ?? DEFAULT_RATING_COMPARE_OPERATION,
      googleRatingVal,
      paymentMethods: paymentMethods ?? paymentMethodOptions[0].value,
      supplierRatingSupplier: supplierRatingSupplier ?? '',
      supplierStarRatingOp: supplierStarRatingOp ?? '',
      supplierStarRatingVal: supplierStarRatingVal ?? '',
      supplierGuestRatingOp: supplierGuestRatingOp ?? '',
      supplierGuestRatingVal: supplierGuestRatingVal ?? '',
      supplierReviewCountMin: supplierReviewCountMin ?? '',
      supplierReviewCountMax: supplierReviewCountMax ?? '',
      propertyType: propertyType ?? BEDBANK_PROPERTY_TYPE_HOTEL,
      holidayTypes: holidayTypes ?? [],
      categories: categories ?? [],
    },
    mode: 'onBlur',
  });

  const queryValue = watch('query');

  // QoL: keep the search type in sync with what's in the box — a pasted UUID means
  // "Property ID"; anything else while on "Property ID" means back to "Name"
  // (an explicit "External ID" selection is never overridden)
  useEffect(() => {
    if (!queryValue) {
      return;
    }
    const isUuid = UUID_PATTERN.test(queryValue.trim());
    const currentType = getValues('type');
    if (isUuid && currentType !== 'id') {
      setValue('type', 'id');
    } else if (!isUuid && currentType === 'id') {
      setValue('type', 'name');
    }
  }, [queryValue, getValues, setValue]);

  const exportProperties = () => {
    handleExportSubmit(getValues());
  };

  const handleReset = () => {
    onReset?.();
    reset();
  };

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
      <CardHeader
        title={<Typography variant="h6">Property Filters</Typography>}
        subheader="Filter and search properties"
        action={
          <Button startIcon={<Refresh />} onClick={handleReset}>
            Reset
          </Button>
        }
      />
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} mb={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Input
                fullWidth
                control={control}
                name="query"
                muiLabel="Search by Property ID, External ID, or Name..."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Select control={control} name="type" muiLabel="Search type">
                {SEARCH_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Grid container spacing={2} mb={3}>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <Select control={control} multiple name="status" muiLabel="Status">
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <Autocomplete
                multiple
                control={control}
                getOptionLabel={(option: CountryOption) => option.label}
                name="country"
                muiLabel="Countries"
                options={countriesOptions}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <Select control={control} muiLabel="Domain" name="domain">
                {BEDBANK_PROPERTY_DOMAINS_ENUM.map((domain) => (
                  <MenuItem key={domain} value={domain} disabled={domain === BEDBANK_PROPERTY_DOMAIN_LE}>
                    {domain}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <Select control={control} name="propertyType" muiLabel="Property Type">
                {PROPERTY_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={{ xs: 12, md: 2.4 }}>
              <Autocomplete
                multiple
                control={control}
                getOptionLabel={(option: string) => option.replace(/_/g, ' ')}
                name="categories"
                muiLabel="Category"
                options={propertyCategories.allCategories}
              />
            </Grid>
          </Grid>
          <Stack spacing={1}>
            <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LocationOn color="action" />
                  <Typography>Location</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Input control={control} name="city" muiLabel="City" fullWidth />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Input control={control} name="stateProvince" muiLabel="State / Province" fullWidth />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Star color="action" />
                  <Typography>Ratings</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Internal Rating
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Select control={control} name="ratingOp" muiLabel="Rating operator">
                          {ratingOperations.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Select control={control} name="ratingVal" muiLabel="Rating value">
                          {ratingValues.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Google Rating
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Select control={control} name="googleRatingCount" muiLabel="Google Rating Count">
                          {guestRatingCountOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Select control={control} name="googleRatingOp" muiLabel="Google Rating operator">
                          {ratingOperations.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Select control={control} name="googleRatingVal" muiLabel="Google Rating value">
                          {ratingValues.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    </Grid>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Supplier Rating
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Select control={control} name="supplierRatingSupplier" muiLabel="Supplier Rating Source">
                          {supplierRatingSupplierOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Select control={control} name="supplierStarRatingOp" muiLabel="Supplier Star Rating Op">
                          {supplierRatingOperations.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Select control={control} name="supplierStarRatingVal" muiLabel="Supplier Star Rating">
                          {supplierRatingOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Select control={control} name="supplierGuestRatingOp" muiLabel="Supplier Guest Rating Op">
                          {supplierRatingOperations.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <Select control={control} name="supplierGuestRatingVal" muiLabel="Supplier Guest Rating">
                          {supplierRatingOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Stack direction="row" spacing={1}>
                          <Input
                            fullWidth
                            control={control}
                            muiLabel="Supplier Min Reviews"
                            name="supplierReviewCountMin"
                            type="number"
                          />
                          <Input
                            fullWidth
                            control={control}
                            muiLabel="Supplier Max Reviews"
                            name="supplierReviewCountMax"
                            type="number"
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Label color="action" />
                  <Typography>Classification & Details</Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Autocomplete
                      multiple
                      control={control}
                      getOptionLabel={(option: string) => option}
                      name="chain"
                      muiLabel="Chain Names"
                      options={chains}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Select control={control} name="promotions" muiLabel="Has promotions?">
                      {booleanOptions.map((option) => (
                        <MenuItem key={option.label} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Select control={control} name="profile" muiLabel="Profile">
                      {BEDBANK_PROFILES_ENUM.map((profile) => (
                        <MenuItem key={profile} value={profile}>
                          {profile}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Select control={control} muiLabel="Comment" name="comment">
                      {BEDBANK_COMMENTS_ENUM.map((comment) => (
                        <MenuItem key={comment} value={comment}>
                          {comment}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Select control={control} name="paymentMethods" muiLabel="Payment Method">
                      {paymentMethodOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Autocomplete
                      multiple
                      control={control}
                      getOptionLabel={(option: string) => option}
                      name="holidayTypes"
                      muiLabel="Holiday Tags"
                      options={holidayTagOptions}
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack>

          <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
            <Box>{footerLeftAction}</Box>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" startIcon={<Download />} onClick={exportProperties}>
                Export
              </Button>
              <Button type="submit" variant="contained" startIcon={<Search />}>
                Search
              </Button>
            </Stack>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
}

export default SearchForm;
